import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';

// Resolved at build time — falsy when VITE_MAINTENANCE_MODE is unset or "false"
const ENV_ON = ['true', '1'].includes(
  String(import.meta.env.VITE_MAINTENANCE_MODE ?? '').toLowerCase()
);

// Resolved at build time — null when VITE_BYPASS_SECRET is unset (bypass disabled)
const BYPASS_SECRET: string | null =
  import.meta.env.VITE_BYPASS_SECRET || null;

const BYPASS_KEY = 'nws_bypass';

export function isBypassActive(): boolean {
  return localStorage.getItem(BYPASS_KEY) === '1';
}

export function clearBypass(): void {
  localStorage.removeItem(BYPASS_KEY);
}

export function useMaintenanceFlag(): { isLoading: boolean; isOn: boolean } {
  const [dbOn, setDbOn] = useState<boolean>(false);
  // Read bypass from localStorage synchronously so returning users never flash.
  const [bypass, setBypass] = useState<boolean>(isBypassActive);

  // Activate bypass when ?nws=<secret> is present in the URL.
  useEffect(() => {
    if (!BYPASS_SECRET) return;
    const params = new URLSearchParams(window.location.search);
    const token = params.get('nws');
    if (token === BYPASS_SECRET) {
      localStorage.setItem(BYPASS_KEY, '1');
      setBypass(true);
      params.delete('nws');
      const qs = params.toString();
      history.replaceState(
        {},
        '',
        window.location.pathname + (qs ? `?${qs}` : ''),
      );
    }
  }, []);

  useEffect(() => {
    if (ENV_ON) return;

    let cancelled = false;
    (async () => {
      try {
        const { data, error } = await supabase
          .from('system_settings')
          .select('value')
          .eq('key', 'maintenance_mode')
          .maybeSingle();

        if (!cancelled) {
          const on = !error && (data?.value === true || data?.value === 'true');
          setDbOn(Boolean(on));
        }
      } catch {
        if (!cancelled) setDbOn(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  return {
    isLoading: false,
    isOn: (ENV_ON || dbOn) && !bypass,
    bypass,
  };
}
